/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useState } from 'react'
import { useIntl } from 'react-intl'

import { EuiButtonIcon, EuiContextMenuPanel, EuiPopover } from '@elastic/eui'

import type { DeploymentSearchResponse } from '@modules/cloud-api/v1/types'
import { useConfig } from '@modules/cui/ConfigContext'

import DeleteDeploymentButton from '@/components/StackDeployments/StackDeploymentAdminActions/StackDeploymentAdminActionsDropdown/DeleteDeploymentButton'
import RestartStoppedDeployment from '@/components/StackDeployments/StackDeploymentAdminActions/StackDeploymentAdminActionsDropdown/RestartStoppedDeployment'
import RestartDeployment from '@/components/StackDeployments/StackDeploymentAdminActions/StackDeploymentAdminActionsDropdown/RestartDeployment'
import { isEsStopped } from '@/lib/stackDeployments/selectors/configurationChanges'
import StopDeployment from '@/components/StackDeployments/StackDeploymentAdminActions/StackDeploymentAdminActionsDropdown/StopDeployment'

type Props = {
  deployment: DeploymentSearchResponse
  onDeploymentAction: () => void
}

export default function DeploymentActionsPopover({ deployment, onDeploymentAction }: Props) {
  const { formatMessage } = useIntl()
  const [isOpen, setIsOpen] = useState(false)
  const isSaasAdminconsole = useConfig('CLOUD_UI_APP') === 'saas-adminconsole'

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleButtonClick = () => {
    setIsOpen((prev) => !prev)
  }

  const handleDeploymentAction = () => {
    onDeploymentAction()
    handleClose()
  }

  const renderRestartButton = () => {
    const stopped = isEsStopped({ deployment })
    const deploymentId = deployment.id

    if (stopped) {
      return (
        <RestartStoppedDeployment
          key={`RestartStoppedDeployment-${deploymentId}`}
          deployment={deployment}
          onRestart={handleDeploymentAction}
        />
      )
    }

    return (
      <RestartDeployment
        key={`RestartDeployment-${deploymentId}`}
        deployment={deployment}
        onRestart={handleDeploymentAction}
      />
    )
  }

  const renderAdminconsoleButton = () => {
    if (isSaasAdminconsole) {
      return [
        <StopDeployment
          key={`StopDeployment-${deployment.id}`}
          deployment={deployment}
          closePopover={handleClose}
        />,
      ]
    }

    return []
  }

  const buttons = [
    renderRestartButton(),
    <DeleteDeploymentButton
      deployment={deployment}
      key={`DeleteDeployment-${deployment.id}`}
      onHideDeploymentInUserconsole={handleDeploymentAction}
    />,
    ...renderAdminconsoleButton(),
  ]

  return (
    <EuiPopover
      button={
        <EuiButtonIcon
          onClick={handleButtonClick}
          iconType='boxesVertical'
          aria-label={formatMessage({
            id: 'deployments-table.actions',
            defaultMessage: 'Deployment actions',
          })}
        />
      }
      isOpen={isOpen}
      closePopover={handleClose}
      anchorPosition='leftCenter'
      panelPaddingSize='none'
    >
      <EuiContextMenuPanel items={buttons} />
    </EuiPopover>
  )
}
